import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, ClipboardPaste, Loader2, MessageCircle, Save, Sparkles, UserCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { PageHeader } from "@/components/Layout";
import { EMPTY_TICKET, TicketForm, toPayload } from "@/components/TicketForm";
import { PriorityBadge } from "@/components/ticketMeta";
import { api, formatApiError } from "@/lib/api";
import { cn } from "@/lib/utils";

const DraftList = ({ drafts, active, onPick }) => (
  <div className="space-y-2" data-testid="ai-drafts-list">
    <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Tichete extrase ({drafts.length})</div>
    {drafts.map((d, i) => (
      <button key={i} data-testid={`ai-draft-${i}`} onClick={() => onPick(d, i)} disabled={d.saved}
        className={cn("flex w-full items-center gap-3 rounded-lg border bg-background/60 p-3 text-left transition-colors",
          i === active ? "border-green-500 ring-1 ring-green-500" : "hover:border-green-500/50", d.saved && "opacity-60")}>
        {d.saved ? <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" /> : <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-green-600 text-[10px] font-bold text-white">{i + 1}</span>}
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold">{d.client_name}</div>
          <div className="truncate text-xs text-muted-foreground">{d.description}</div>
        </div>
        <PriorityBadge priority={d.priority} />
      </button>
    ))}
  </div>
);

const WhatsAppPanel = ({ raw, setRaw, onParse, parsing, drafts, active, onPick }) => {
  const pasteClipboard = async () => {
    try {
      setRaw(await navigator.clipboard.readText());
    } catch (e) {
      toast.error("Nu am acces la clipboard. Lipește manual (Ctrl+V / apăsare lungă).");
    }
  };
  return (
    <div className="space-y-4 rounded-xl border border-green-500/30 bg-green-500/5 p-5 dark:bg-emerald-950/40 sm:p-6" data-testid="whatsapp-ai-parser-section">
      <div className="flex items-center gap-3">
        <div className="grid h-10 w-10 place-items-center rounded-lg bg-green-600 text-white"><MessageCircle className="h-5 w-5" /></div>
        <div>
          <h2 className="text-lg font-bold tracking-tight">Import din WhatsApp</h2>
          <p className="text-xs text-muted-foreground">Copiază mesajul din grupul „service” și lipește-l mai jos.</p>
        </div>
      </div>
      <Textarea data-testid="whatsapp-message-input" rows={8} value={raw} onChange={(e) => setRaw(e.target.value)}
        placeholder={"ex: [12.06, 09:14] Andrei: Doamna Ionescu, str. Florilor 12 Ploiești, 0722 123 456 — centrala Ariston Genus nu mai pornește, eroare 501. Urgent, nu au apă caldă."}
        className="bg-background/70 font-mono text-sm" />
      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="outline" onClick={pasteClipboard} data-testid="paste-clipboard-button">
          <ClipboardPaste className="h-4 w-4" /> Lipește
        </Button>
        <Button type="button" onClick={onParse} disabled={parsing || raw.trim().length < 3} data-testid="ai-parse-button"
          className="flex-1 bg-green-600 text-white hover:bg-green-700">
          {parsing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          {parsing ? "Analizez..." : "Analizează cu AI"}
        </Button>
      </div>
      {drafts.length > 0 && <DraftList drafts={drafts} active={active} onPick={onPick} />}
    </div>
  );
};

export default function NewTicket() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState(EMPTY_TICKET);
  const [techs, setTechs] = useState([]);
  const [raw, setRaw] = useState("");
  const [drafts, setDrafts] = useState([]);
  const [active, setActive] = useState(-1);
  const [parsing, setParsing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [matched, setMatched] = useState("");

  useEffect(() => {
    api.get("/users").then((r) => setTechs(r.data));
    const cid = params.get("client");
    if (cid) {
      api.get(`/clients/${cid}`).then(({ data: { client: c } }) => {
        setForm((f) => ({ ...f, client_id: c.id, client_name: c.name, phone: c.phone, address: c.address }));
        setMatched(c.name);
      });
    }
  }, [params]);

  const loadDraft = (d, i) => {
    const { saved, matched_client_name, ...fields } = d;
    setActive(i);
    setMatched(matched_client_name || "");
    setForm({ ...EMPTY_TICKET, ...fields, source: "whatsapp", raw_message: raw });
  };

  const parse = async () => {
    setParsing(true);
    try {
      const { data } = await api.post("/ai/parse", { text: raw });
      setDrafts(data.tickets.map((d) => ({ ...d, saved: false })));
      loadDraft(data.tickets[0], 0);
      toast.success(`${data.tickets.length} tichet(e) extrase. Verifică datele și salvează.`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setParsing(false);
    }
  };

  const save = async () => {
    if (!form.client_name.trim() || !form.description.trim()) {
      toast.error("Completează numele clientului și descrierea problemei");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/tickets", toPayload(form));
      toast.success(`Tichetul ${data.code} a fost creat`);
      if (active >= 0) {
        const next = drafts.map((d, i) => (i === active ? { ...d, saved: true } : d));
        setDrafts(next);
        const ni = next.findIndex((d) => !d.saved);
        if (ni >= 0) { loadDraft(next[ni], ni); return; }
      }
      nav(`/tichete/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="new-ticket-page">
      <PageHeader title="Tichet nou" subtitle="Importă din WhatsApp sau completează manual." />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
        <div className="lg:col-span-2">
          <div className="lg:sticky lg:top-8">
            <WhatsAppPanel raw={raw} setRaw={setRaw} onParse={parse} parsing={parsing} drafts={drafts} active={active} onPick={loadDraft} />
          </div>
        </div>
        <div className="rounded-xl border bg-card p-5 sm:p-6 lg:col-span-3">
          {matched && (
            <div className="mb-6 flex items-center gap-2 rounded-lg border border-primary/30 bg-primary/10 px-3 py-2 text-sm" data-testid="matched-client-info">
              <UserCheck className="h-4 w-4 text-primary" /> Client existent: <b>{matched}</b>
            </div>
          )}
          <TicketForm value={form} onChange={setForm} technicians={techs} />
          <div className="mt-8 flex flex-col-reverse gap-2 border-t pt-6 sm:flex-row sm:justify-end">
            <Button variant="ghost" onClick={() => { setForm(EMPTY_TICKET); setMatched(""); setActive(-1); }} data-testid="reset-form-button">Golește</Button>
            <Button onClick={save} disabled={saving} className="h-11 px-6 font-semibold" data-testid="save-ticket-button">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salvează tichet
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
